import {
  compactIban,
  czechAccountToIban,
  formatCzechAccount,
  ibanToCzechAccount,
  isValidCzechAccount,
  isValidIban,
  parseCzechAccount,
} from "@/lib/banking/czechAccount";
import { buildSPD } from "@/lib/invoices/paymentQR";

/**
 * The free QR payment generator (`/qr-platba-generator`): a form's raw text to
 * a Czech QR payment (SPD 1.0, https://qr-platba.cz/pro-vyvojare/), or the
 * fields that are wrong. Pure and offline: nothing leaves the browser.
 *
 * The account is a Czech account number (`19-2000145399/0800`, checked with
 * the CNB's mod-11 rule and turned into an IBAN) or a Czech or Slovak IBAN.
 * SPD's text fields are kept to plain ASCII by the shared builder, so Czech
 * letters are transliterated first ("Nájem říjen" → "NAJEM RIJEN") rather
 * than dropped.
 */

export interface QrPaymentInput {
  account: string;
  /** As typed: "1 234,50" or "1234.5". */
  amount: string;
  currency: "CZK" | "EUR";
  variableSymbol: string;
  constantSymbol: string;
  specificSymbol: string;
  message: string;
  recipientName: string;
  /** `YYYY-MM-DD` from a date input, or "". */
  dueDate: string;
}

export type QrPaymentError =
  | "too_long"
  | "account_required"
  | "account_invalid"
  | "iban_invalid"
  | "iban_country"
  | "amount_invalid"
  | "amount_range"
  | "symbol_invalid"
  | "date_invalid";

export type QrPaymentErrors = Partial<Record<keyof QrPaymentInput, QrPaymentError>>;

export type QrPaymentResult =
  | {
      ok: true;
      spd: string;
      iban: string;
      /** The Czech account, when the IBAN is Czech. */
      account: string | null;
      amount: number;
    }
  | { ok: false; errors: QrPaymentErrors };

/**
 * The longest text each field takes, as typed (spaces included). The form
 * uses the same numbers for maxLength; anything longer is refused here too,
 * so pasted input cannot grow the payload.
 */
export const QR_FIELD_MAX: Record<Exclude<keyof QrPaymentInput, "currency">, number> = {
  account: 42,
  amount: 20,
  variableSymbol: 14,
  constantSymbol: 14,
  specificSymbol: 14,
  message: 60,
  recipientName: 35,
  dueDate: 10,
};

/** The largest amount SPD's AM field takes (10 characters with two decimals). */
export const MAX_AMOUNT = 9_999_999.99;

/** "1 234,50", "1234.5", "1 234" (no-break spaces too) to a number, or NaN. */
export function parseAmount(raw: string): number {
  const compact = raw.replace(/[\s\u00a0\u202f]/g, "");
  if (!/^\d+([.,]\d{1,2})?$/.test(compact)) return Number.NaN;
  return Number(compact.replace(",", "."));
}

/** Czech and Slovak letters to their ASCII base ("Žluťoučký" → "Zlutoucky"). */
export function transliterate(text: string): string {
  return text.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

function resolveAccount(raw: string): { iban: string; account: string | null } | QrPaymentError {
  const value = raw.trim();
  if (!value) return "account_required";
  if (/^[A-Za-z]{2}/.test(value.replace(/\s+/g, ""))) {
    const iban = compactIban(value);
    if (!isValidIban(iban)) return "iban_invalid";
    if (!/^(CZ|SK)/.test(iban)) return "iban_country";
    const czech = ibanToCzechAccount(iban);
    return { iban, account: czech ? formatCzechAccount(czech) : null };
  }
  const account = parseCzechAccount(value);
  if (!account || !isValidCzechAccount(account)) return "account_invalid";
  return { iban: czechAccountToIban(account), account: formatCzechAccount(account) };
}

function isDate(value: string): boolean {
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return false;
  const date = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return date.toISOString().slice(0, 10) === value;
}

export function buildQrPayment(input: QrPaymentInput): QrPaymentResult {
  const errors: QrPaymentErrors = {};
  for (const [field, max] of Object.entries(QR_FIELD_MAX) as [
    keyof typeof QR_FIELD_MAX,
    number,
  ][]) {
    if ((input[field] ?? "").length > max) errors[field] = "too_long";
  }
  if (input.currency !== "CZK" && input.currency !== "EUR") errors.currency = "too_long";
  if (Object.keys(errors).length > 0) return { ok: false, errors };

  const resolved = resolveAccount(input.account);
  if (typeof resolved === "string") errors.account = resolved;

  const amount = parseAmount(input.amount);
  if (Number.isNaN(amount)) errors.amount = "amount_invalid";
  else if (amount <= 0 || amount > MAX_AMOUNT) errors.amount = "amount_range";

  for (const field of ["variableSymbol", "constantSymbol", "specificSymbol"] as const) {
    const digits = input[field].replace(/\s+/g, "");
    if (digits && !/^\d{1,10}$/.test(digits)) errors[field] = "symbol_invalid";
  }

  if (input.dueDate && !isDate(input.dueDate)) errors.dueDate = "date_invalid";

  if (Object.keys(errors).length > 0 || typeof resolved === "string") return { ok: false, errors };

  const spd = buildSPD({
    iban: resolved.iban,
    amount,
    currency: input.currency,
    variableSymbol: input.variableSymbol.replace(/\s+/g, "") || undefined,
    constantSymbol: input.constantSymbol.replace(/\s+/g, "") || undefined,
    specificSymbol: input.specificSymbol.replace(/\s+/g, "") || undefined,
    message: transliterate(input.message) || undefined,
    spdRecipientName: transliterate(input.recipientName) || undefined,
    dueDate: input.dueDate || undefined,
  });
  return { ok: true, spd, iban: resolved.iban, account: resolved.account, amount };
}
